import { useMemo } from 'react'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import { useRouter } from 'next/router'

import BodyText from '../../../../app/components/BodyText'
import {
  clientContextCustomerNameSelector,
  clientContextHasCompanyProfilesSelector,
  clientContextIsAnonymousCustomerSelector,
  clientContextIsCompanyUserSelector,
  clientContextProfileNameSelector,
} from '../../../../clientContext/store/clientContextSelectors'
import Button from '../../../../app/components/Button'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import {
  authCurrentUserProfileTypeSelector,
  authCustomerIsLoggedInSelector,
  authSignOutPendingSelector,
  authPendingSignOutCheckoutUpdateSelector,
} from '../../../../auth/store/authSelectors'
import Dropdown from '../../../../app/components/dropdown/Dropdown'
import { DropdownButtonProps } from '../../../../app/components/dropdown/DropdownButton'
import DropdownIcon from '../../../../app/components/DropdownIcon'
import {
  setAuthProfileSelectionModalType,
  setAuthGuestCustomerOnboardingModalVisible,
} from '../../../../auth/store/authSlice'
import AuthProfileTypeIcon from '../../../../auth/components/AuthProfileTypeIcon'
import { AppRoutes, AppTestIds } from '../../../../app/constants'
import isAppWhiteLabeled from '../../../../app/utils/isAppWhiteLabeled'
import useRouteMatch from '../../../../app/hooks/useRouteMatch'
import {
  preOrderUserNameSelector,
  preOrderUserSelector,
} from '../../../../preOrder/store/preOrderSelectors'
import { ProfileSelectionModalType } from '../../../../auth/types'
import useAppSignOut from '../../../../auth/hooks/useAppSignOut'
import { ProfileAction } from '../../../constants'

import styles from './NavbarUserProfile.module.scss'

function ProfileDropdownButton({
  className,
  isOpen,
  onToggle,
}: DropdownButtonProps) {
  const { t } = useTranslation('common')

  const signOutPending = useAppSelector(authSignOutPendingSelector)

  const pendingSignOutCheckoutUpdate = useAppSelector(
    authPendingSignOutCheckoutUpdateSelector,
  )

  const customerName = useAppSelector(clientContextCustomerNameSelector)

  const profileName = useAppSelector(clientContextProfileNameSelector)

  const isCompanyUser = useAppSelector(clientContextIsCompanyUserSelector)

  const authProfileType = useAppSelector(authCurrentUserProfileTypeSelector)

  const preOrderUserName = useAppSelector(preOrderUserNameSelector)

  const isAnonymousCustomer = useAppSelector(
    clientContextIsAnonymousCustomerSelector,
  )
  const isLoggedIn = useAppSelector(authCustomerIsLoggedInSelector)

  const isOutletDetailsPage = useRouteMatch([`${AppRoutes.Outlet}/`])

  function getProfileName() {
    if (isLoggedIn) {
      return profileName
    }

    if (preOrderUserName) {
      return preOrderUserName
    }

    return null
  }

  function getProfileAdditionalInfo() {
    if (isCompanyUser) {
      return customerName
    }

    if (isAnonymousCustomer) {
      return t('auth.guest')
    }

    return null
  }

  const profileAdditionalInfo = getProfileAdditionalInfo()

  return (
    <Button
      size="large"
      variant="secondary"
      onClick={onToggle}
      disabled={signOutPending || pendingSignOutCheckoutUpdate}
      className={className}
      data-testid={AppTestIds.NavBar.Button.ProfileDropdown}
    >
      <AuthProfileTypeIcon
        type={authProfileType}
        className={styles.profileIcon}
      />
      <span
        className={clsx(styles.profileContent, {
          [styles.outletDetailsPage]: isOutletDetailsPage,
        })}
      >
        <BodyText
          as="span"
          className={styles.profileName}
          data-testid={AppTestIds.NavBar.Button.ProfileName}
        >
          {getProfileName()}
        </BodyText>
        {!!profileAdditionalInfo && (
          <BodyText as="span" className={styles.profileUserName}>
            {profileAdditionalInfo}
          </BodyText>
        )}
      </span>
      <DropdownIcon isOpen={isOpen} />
    </Button>
  )
}

export default function NavbarUserProfile() {
  const { t } = useTranslation('common')

  const dispatch = useAppDispatch()

  const router = useRouter()

  const appSignOut = useAppSignOut()

  const isWhiteLabeled = isAppWhiteLabeled()

  const isAnonymousCustomer = useAppSelector(
    clientContextIsAnonymousCustomerSelector,
  )
  const isLoggedIn = useAppSelector(authCustomerIsLoggedInSelector)
  const preOrderUser = useAppSelector(preOrderUserSelector)

  const hasCompanyProfiles = useAppSelector(
    clientContextHasCompanyProfilesSelector,
  )

  const navBarProfileOptions = useMemo(() => {
    const options = [
      !!isLoggedIn && {
        label: t('components.navBar.profile.myOrders'),
        value: ProfileAction.MyOrders,
      },
      !!isLoggedIn &&
        !isAnonymousCustomer && {
          label: t('components.navBar.profile.myAccount'),
          value: ProfileAction.MyAccount,
        },
      !!isLoggedIn &&
        !isAnonymousCustomer && {
          label: t('components.navBar.profile.myWallets'),
          value: ProfileAction.MyWallets,
        },
      hasCompanyProfiles && {
        label: t('components.navBar.profile.changeProfile'),
        value: ProfileAction.ChangeProfile,
      },
      isAnonymousCustomer &&
        (!preOrderUser || !preOrderUser.isHost) && {
          label: isAppWhiteLabeled()
            ? t('components.navBar.profile.createProfile')
            : t('components.navBar.profile.createAnconProfile'),
          value: ProfileAction.GuestOnBoarding,
        },
      {
        label:
          isAnonymousCustomer || (preOrderUser && !isLoggedIn)
            ? t('components.navBar.profile.exitSession')
            : t('components.navBar.profile.signOut'),
        value: ProfileAction.SignOut,
      },
    ]

    return options.filter(Boolean) as Array<{
      label: string
      value: ProfileAction
    }>
  }, [hasCompanyProfiles, isAnonymousCustomer, isLoggedIn, preOrderUser, t])

  function handleSignOut() {
    appSignOut()
  }

  function handleNavigateMyWallets() {
    const isAppWL = isAppWhiteLabeled()
    const walletsURL = isAppWL ? AppRoutes.WalletsRedirect : AppRoutes.Wallets
    router.push(walletsURL)
  }

  function handleClickOption(option: string) {
    switch (option) {
      case ProfileAction.MyOrders:
        router.push(AppRoutes.Orders)
        break

      case ProfileAction.MyAccount:
        router.push(AppRoutes.Account)
        break

      case ProfileAction.MyWallets:
        handleNavigateMyWallets()
        break

      case ProfileAction.ChangeProfile:
        dispatch(
          setAuthProfileSelectionModalType(
            ProfileSelectionModalType.ProfileSelection,
          ),
        )
        break

      case ProfileAction.SignOut:
        handleSignOut()
        break

      case ProfileAction.GuestOnBoarding:
        dispatch(setAuthGuestCustomerOnboardingModalVisible(true))
        break

      default:
        break
    }
  }

  return (
    <Dropdown
      CustomDropdownButton={ProfileDropdownButton}
      buttonClassName={clsx(styles.profileDropdownButton, {
        [styles.whiteLabeled]: isWhiteLabeled,
      })}
      popupPlacement="top-end"
      popupClassName={clsx(styles.profileDropdownPopup, {
        [styles.guestCustomer]: isAnonymousCustomer,
      })}
      onClickOption={handleClickOption}
      options={navBarProfileOptions}
    />
  )
}
